import {
  AUSGABEN,
  BANKGEBUEHREN,
  EINNAHME,
  FINANZAMT,
  IGNORIEREN,
  PAUSCHALEN,
  PRIVAT,
  VERRECHNET,
  cent,
  type Buchung,
  type Konto,
  type Rechnung,
  type Regel,
  type Sonderfall,
  type Steuerjahr,
  type StripeUmsatz,
  type Tag,
} from "./typen";

export type Art =
  | "offen"
  | "Einnahme"
  | "Ausgabe"
  | "Finanzamt"
  | "Privat"
  | "Ignoriert"
  | "Pauschale";

// Eine Rechenzeile. Aus einer Buchung können mehrere werden (Aufteilung, Anteil).
export type Posten = {
  id: string;
  buchungId: number | null;
  datum: string | null; // Zufluss/Abfluss, gilt für die EÜR
  ustDatum: string | null; // gilt für die UStVA; bei Soll-Rechnungen der Leistungstag
  text: string;
  partner: string;
  kategorie: string;
  brutto: number; // Cent
  satz: number;
  beleg: string;
  konto: string;
  kontoPrivat: boolean;
  kontoBetrag: number | null;
  anteil: number;
  sonderfall: Sonderfall | null;
  duplikat: boolean;
  unsicher: string[];
  entschieden: boolean;
  vorschlag: Regel | null;
  quelle: string;
};

export type Rechenbasis = {
  jahr: Steuerjahr;
  konten: Konto[];
  buchungen: Buchung[];
  regeln: Regel[];
  rechnungen: Rechnung[];
  stripe: StripeUmsatz[];
  tage: Tag[];
};

export const artVon = (kategorie: string): Art => {
  if (!kategorie) return "offen";
  if (kategorie === EINNAHME) return "Einnahme";
  if (kategorie === FINANZAMT) return "Finanzamt";
  if (kategorie === PRIVAT) return "Privat";
  if (kategorie === IGNORIEREN || kategorie === VERRECHNET) return "Ignoriert";
  if (kategorie === PAUSCHALEN) return "Pauschale";
  return "Ausgabe";
};

// Ganzzahlig gerechnet, damit Cent nicht durch Gleitkomma kippen. Entspricht ROUND(E/(1+F),2).
export const nettoVon = (brutto: number, satz: number) =>
  Math.sign(brutto) * Math.round((Math.abs(brutto) * 100) / (100 + satz));
export const ustVon = (brutto: number, satz: number) =>
  brutto - nettoVon(brutto, satz);

export const regelFinden = (partner: string, text: string, regeln: Regel[]) => {
  const heuhaufen = `${partner} ${text}`.toLowerCase();
  return regeln.find((r) => heuhaufen.includes(r.muster.toLowerCase())) ?? null;
};

const dupSchluessel = (b: Buchung) =>
  `${b.konto_id ?? "-"}|${b.datum}|${cent(b.betrag)}|${(b.text || b.partner).toLowerCase()}`;

export const duplikateFinden = (buchungen: Buchung[]) => {
  const anzahl = new Map<string, number>();
  for (const b of buchungen)
    anzahl.set(dupSchluessel(b), (anzahl.get(dupSchluessel(b)) ?? 0) + 1);
  return new Set(
    buchungen
      .filter((b) => (anzahl.get(dupSchluessel(b)) ?? 0) > 1)
      .map((b) => b.id),
  );
};

const buchungZuPosten = (
  b: Buchung,
  basis: Rechenbasis,
  duplikate: Set<number>,
): Posten[] => {
  const konto = basis.konten.find((k) => k.id === b.konto_id);
  const kontoPrivat = konto?.art === "privat";
  const regel = regelFinden(b.partner, b.text, basis.regeln);
  const brutto = cent(b.betrag);
  const entschieden = !!b.kategorie;
  const grund: Posten = {
    id: `b${b.id}`,
    buchungId: b.id,
    datum: b.datum,
    ustDatum: b.datum,
    text: b.text || b.partner,
    partner: b.partner,
    kategorie: "",
    brutto,
    satz: basis.jahr.satz_regel,
    beleg: b.beleg ?? "",
    konto:
      konto?.name ??
      (b.quelle === "einlage"
        ? "Privateinlage"
        : b.quelle === "bar"
          ? "Bar"
          : ""),
    kontoPrivat,
    kontoBetrag: brutto,
    anteil: 100,
    sonderfall: b.sonderfall,
    duplikat: !entschieden && duplikate.has(b.id),
    unsicher: [],
    entschieden,
    vorschlag: regel,
    quelle: b.quelle,
  };

  if (b.teile?.length) {
    const teile = b.teile.map((t) => ({ ...t, brutto: cent(t.betrag) }));
    if (teile.reduce((s, t) => s + t.brutto, 0) !== brutto) {
      return [
        {
          ...grund,
          entschieden: false,
          unsicher: [
            `Teilbeträge ergeben nicht ${(brutto / 100).toFixed(2)} €`,
          ],
        },
      ];
    }
    return teile.map((t, i) => ({
      ...grund,
      id: `b${b.id}.${i + 1}`,
      kategorie: t.kategorie,
      satz: t.satz,
      brutto: t.brutto,
    }));
  }

  if (entschieden) {
    const voll = {
      ...grund,
      kategorie: b.kategorie ?? "",
      satz: b.satz ?? basis.jahr.satz_regel,
    };
    if (b.anteil >= 100 || voll.kategorie === PRIVAT) return [voll];
    const geschaeftlich = Math.round((brutto * b.anteil) / 100);
    const posten: Posten[] = [
      { ...voll, brutto: geschaeftlich, anteil: b.anteil },
    ];
    posten.push({
      ...voll,
      id: `b${b.id}.privat`,
      kategorie: PRIVAT,
      satz: 0,
      brutto: brutto - geschaeftlich,
      anteil: 100 - b.anteil,
    });
    return posten;
  }

  // Auf Privatkonten ist die Regel nur Vorschlag: jede Zeile wird durchgesehen.
  if (kontoPrivat) {
    return [
      {
        ...grund,
        unsicher: ["Privatkonto: als privat oder geschäftlich markieren"],
      },
    ];
  }
  if (regel) {
    return [
      {
        ...grund,
        kategorie: regel.kategorie,
        satz: regel.satz,
        sonderfall: b.sonderfall ?? regel.sonderfall,
        unsicher: regel.unsicher
          ? [regel.hinweis || "Regel ist als unsicher markiert"]
          : [],
      },
    ];
  }
  // Nicht raten: ohne Regel bleibt die Kategorie leer und zählt nicht mit.
  return [
    {
      ...grund,
      unsicher: ["Kein Partner bekannt: Kategorie und Satz bitte festlegen"],
    },
  ];
};

const rechnungZuPosten = (r: Rechnung, jahr: Steuerjahr): Posten[] => {
  const bezahlt = r.status === "bezahlt" ? r.bezahlt_am : null;
  const ustDatum =
    jahr.besteuerung === "ist"
      ? bezahlt
      : (r.leistungsdatum ?? r.rechnungsdatum);
  if (!ustDatum && !bezahlt) return [];
  const teile: [number, number][] = [
    [cent(r.brutto_19), jahr.satz_regel],
    [cent(r.brutto_7), jahr.satz_ermaessigt],
    [cent(r.brutto_0), 0],
  ];
  return teile
    .filter(([brutto]) => brutto !== 0)
    .map(([brutto, satz]) => ({
      id: `r${r.id}.${satz}`,
      buchungId: null,
      datum: bezahlt,
      ustDatum,
      text: `Rechnung ${r.nummer}${r.kunde ? ` ${r.kunde}` : ""}`,
      partner: r.kunde,
      kategorie: EINNAHME,
      brutto,
      satz,
      beleg: r.nummer,
      konto: "",
      kontoPrivat: false,
      kontoBetrag: null,
      anteil: 100,
      sonderfall: null,
      duplikat: false,
      unsicher: [],
      entschieden: true,
      vorschlag: null,
      quelle: "rechnung",
    }));
};

const stripeZuPosten = (s: StripeUmsatz): Posten[] => {
  const basis = {
    buchungId: null,
    datum: s.datum,
    ustDatum: s.datum,
    partner: "Stripe",
    beleg: s.beleg ?? "",
    konto: "Stripe",
    kontoPrivat: false,
    kontoBetrag: null,
    anteil: 100,
    duplikat: false,
    entschieden: true,
    vorschlag: null,
    quelle: "stripe",
  };
  const posten: Posten[] = [
    {
      ...basis,
      id: `s${s.id}`,
      text: `Stripe-Umsatz ${s.beschreibung}`.trim(),
      kategorie: EINNAHME,
      brutto: cent(s.brutto),
      satz: s.satz,
      sonderfall: null,
      unsicher: [],
    },
  ];
  if (s.gebuehr) {
    posten.push({
      ...basis,
      id: `s${s.id}.gebuehr`,
      text: `Stripe-Gebühr ${s.beschreibung}`.trim(),
      kategorie: BANKGEBUEHREN,
      brutto: -cent(s.gebuehr),
      satz: 0,
      sonderfall: "13b",
      unsicher: [],
    });
  }
  return posten;
};

export const pauschalenRechnen = (jahr: Steuerjahr, tage: Tag[]) => {
  const imJahr = tage.filter((t) => t.datum.startsWith(`${jahr.jahr}-`));
  const km = imJahr
    .filter((t) => t.art === "geschaeft")
    .reduce((s, t) => s + (t.km ?? 0), 0);
  const pendelTage = imJahr.filter((t) => t.art === "pendel").length;
  const homeTage = imJahr.filter((t) => t.art === "homeoffice").length;
  const geschaeft = Math.round(km * jahr.km_satz * 100);
  const pendel = Math.round(
    pendelTage * jahr.entfernung_km * jahr.pendel_satz * 100,
  );
  const homeoffice = Math.min(
    cent(homeTage * jahr.homeoffice_tag),
    cent(jahr.homeoffice_max),
  );
  return {
    km,
    pendelTage,
    homeTage,
    geschaeft,
    pendel,
    homeoffice,
    summe: geschaeft + pendel + homeoffice,
  };
};

const pauschalePosten = (
  id: string,
  jahr: number,
  text: string,
  brutto: number,
): Posten => ({
  id,
  buchungId: null,
  datum: `${jahr}-12-31`,
  ustDatum: `${jahr}-12-31`,
  text,
  partner: "",
  kategorie: PAUSCHALEN,
  brutto: -brutto,
  satz: 0,
  beleg: "",
  konto: "",
  kontoPrivat: false,
  kontoBetrag: null,
  anteil: 100,
  sonderfall: null,
  duplikat: false,
  unsicher: [],
  entschieden: true,
  vorschlag: null,
  quelle: "pauschale",
});

export const postenBilden = (basis: Rechenbasis): Posten[] => {
  const duplikate = duplikateFinden(basis.buchungen);
  const p = pauschalenRechnen(basis.jahr, basis.tage);
  const j = basis.jahr.jahr;
  const pauschalen: [string, number][] = [
    ["Geschäftsfahrten", p.geschaeft],
    ["Fahrten Wohnung–Betrieb", p.pendel],
    ["Homeoffice", p.homeoffice],
  ];
  let posten = [
    ...basis.buchungen.flatMap((b) => buchungZuPosten(b, basis, duplikate)),
    ...basis.rechnungen.flatMap((r) => rechnungZuPosten(r, basis.jahr)),
    ...basis.stripe.flatMap(stripeZuPosten),
    ...pauschalen
      .filter(([, c]) => c)
      .map(([t, c], i) => pauschalePosten(`p${i}`, j, t, c)),
  ];
  // Ohne Verzicht gilt die Kleinunternehmerregelung: keine USt, keine Vorsteuer.
  if (!basis.jahr.kur_verzicht) posten = posten.map((x) => ({ ...x, satz: 0 }));
  return posten
    .filter(
      (x) => x.datum?.startsWith(`${j}-`) || x.ustDatum?.startsWith(`${j}-`),
    )
    .sort(
      (a, b) =>
        (a.datum ?? a.ustDatum ?? "").localeCompare(
          b.datum ?? b.ustDatum ?? "",
        ) || a.id.localeCompare(b.id),
    );
};

export type UstvaWerte = {
  kz81: number; // volle Euro
  kz86: number; // volle Euro
  ust81: number; // Cent
  ust86: number; // Cent
  kz66: number; // Cent
  zahllast: number; // Cent
  kontrolleUst: number; // Cent
  bewegung: boolean;
  bemessung13b: number; // Cent, Prüfwert
  bemessungIge: number; // Cent, Prüfwert
  ust13b: number; // Cent, Prüfwert
  ustIge: number; // Cent, Prüfwert
};

const monatVon = (datum: string) => Number(datum.slice(5, 7));

export const ustvaRechnen = (
  posten: Posten[],
  jahr: Steuerjahr,
  filter: (monat: number) => boolean = () => true,
): UstvaWerte => {
  const drin = posten.filter(
    (p) =>
      p.ustDatum?.startsWith(`${jahr.jahr}-`) && filter(monatVon(p.ustDatum)),
  );
  const netto = (satz: number) =>
    drin
      .filter((p) => artVon(p.kategorie) === "Einnahme" && p.satz === satz)
      .reduce((s, p) => s + nettoVon(p.brutto, p.satz), 0);
  const kz81 = Math.trunc(netto(jahr.satz_regel) / 100);
  const kz86 = Math.trunc(netto(jahr.satz_ermaessigt) / 100);
  const ust81 = Math.round(kz81 * jahr.satz_regel);
  const ust86 = Math.round(kz86 * jahr.satz_ermaessigt);
  const kz66 = 0 - drin
    .filter((p) => artVon(p.kategorie) === "Ausgabe")
    .reduce((s, p) => s + ustVon(p.brutto, p.satz), 0);
  const sonder = (fall: Sonderfall) =>
    Math.abs(
      drin
        .filter((p) => p.sonderfall === fall)
        .reduce((s, p) => s + nettoVon(p.brutto, p.satz), 0),
    );
  const bemessung13b = sonder("13b");
  const bemessungIge = sonder("ige");
  return {
    kz81,
    kz86,
    ust81,
    ust86,
    kz66,
    zahllast: ust81 + ust86 - kz66,
    kontrolleUst: drin
      .filter((p) => artVon(p.kategorie) === "Einnahme")
      .reduce((s, p) => s + ustVon(p.brutto, p.satz), 0),
    bewegung: drin.some((p) =>
      ["Einnahme", "Ausgabe"].includes(artVon(p.kategorie)),
    ),
    bemessung13b,
    bemessungIge,
    ust13b: Math.round((bemessung13b * jahr.satz_regel) / 100),
    ustIge: Math.round((bemessungIge * jahr.satz_regel) / 100),
  };
};

export const MONATE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

export const ustvaMonat = (posten: Posten[], jahr: Steuerjahr, monat: number) =>
  ustvaRechnen(posten, jahr, (m) => m === monat);
export const ustvaQuartal = (
  posten: Posten[],
  jahr: Steuerjahr,
  quartal: number,
) => ustvaRechnen(posten, jahr, (m) => Math.ceil(m / 3) === quartal);

export type EuerZeile = { position: string; betrag: number };

export const euerRechnen = (posten: Posten[], jahr: Steuerjahr) => {
  const drin = posten.filter((p) => p.datum?.startsWith(`${jahr.jahr}-`));
  const nachArt = (art: Art) => drin.filter((p) => artVon(p.kategorie) === art);
  const nettoSumme = (l: Posten[]) =>
    l.reduce((s, p) => s + nettoVon(p.brutto, p.satz), 0);
  const ustSumme = (l: Posten[]) =>
    l.reduce((s, p) => s + ustVon(p.brutto, p.satz), 0);
  const zeilen: EuerZeile[] = [
    {
      position: "Betriebseinnahmen netto",
      betrag: nettoSumme(nachArt("Einnahme")),
    },
    { position: "Vereinnahmte USt", betrag: ustSumme(nachArt("Einnahme")) },
    ...AUSGABEN.map((k) => ({
      position: `${k} (netto)`,
      betrag: nettoSumme(drin.filter((p) => p.kategorie === k)),
    })),
    { position: "Gezahlte Vorsteuer", betrag: ustSumme(nachArt("Ausgabe")) },
    { position: PAUSCHALEN, betrag: nettoSumme(nachArt("Pauschale")) },
    {
      position: "Ans Finanzamt gezahlte USt (Erstattung positiv)",
      betrag: nettoSumme(nachArt("Finanzamt")),
    },
  ];
  const gewinn = zeilen.reduce((s, z) => s + z.betrag, 0);
  const brutto = (l: Posten[]) => l.reduce((s, p) => s + p.brutto, 0);
  return {
    zeilen,
    gewinn,
    privat: brutto(nachArt("Privat")),
    offen: brutto(nachArt("offen")),
    einlagen: -brutto(
      drin.filter(
        (p) =>
          (p.kontoPrivat || p.quelle === "einlage") &&
          artVon(p.kategorie) === "Ausgabe",
      ),
    ),
    kontrolle:
      gewinn -
      brutto([
        ...nachArt("Einnahme"),
        ...nachArt("Ausgabe"),
        ...nachArt("Finanzamt"),
        ...nachArt("Pauschale"),
      ]),
  };
};
